import { Button, colors, Field, Icon, LogoWordmark, Screen, Stack, Text, useAction, useApi, useRouter, useSession } from '@khaddi/ui';
import { useEffect, useState } from 'react';
import { Pressable, View } from 'react-native';
import { InlineError } from '../components/bits';

const PHONE = /^0\d{9}$/;
const SIX_DIGITS = /^\d{6}$/;

/** Only in-app paths are accepted as `next`, so a crafted link can't bounce elsewhere. */
const safeNext = (next: string | undefined): string => (next && next.startsWith('/') && !next.startsWith('//') ? next : '/');

const digits = (t: string) => t.replace(/\D/g, '').slice(0, 6);

/**
 * phone → pin (has a PIN) | register (new, or an account that needs a counter code to set its PIN) | reset (forgot PIN).
 * No SMS: the counter code printed on the receipt proves the phone.
 */
type Step = 'phone' | 'pin' | 'register' | 'reset';

const HEADINGS: Record<Step, { kicker: string; title: string }> = {
  phone: { kicker: 'Chào bạn,', title: 'Đăng nhập thành viên' },
  pin: { kicker: 'Chào mừng trở lại', title: 'Nhập mã PIN' },
  register: { kicker: 'Tạo tài khoản', title: 'Đặt mã PIN của bạn' },
  reset: { kicker: 'Quên mã PIN', title: 'Đặt lại bằng mã tại quầy' },
};

export const LoginScreen = () => {
  const api = useApi();
  const { user, signIn } = useSession();
  const { route, navigate, back } = useRouter();
  const next = safeNext(route.query.next);

  const [step, setStep] = useState<Step>('phone');
  const [needsCode, setNeedsCode] = useState(false);
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [pin, setPin] = useState('');
  const [pinAgain, setPinAgain] = useState('');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');

  useEffect(() => {
    if (user) navigate(next, { replace: true });
  }, [user, navigate, next]);

  const goTo = (target: Step) => {
    setPin('');
    setPinAgain('');
    setCode('');
    setStep(target);
  };

  const status = useAction(async () => {
    const { state } = await api.auth.phoneStatus(phone);
    setNeedsCode(state === 'no_pin');
    goTo(state === 'has_pin' ? 'pin' : 'register');
  });
  const login = useAction(async () => signIn(await api.auth.loginWithPin(phone, pin)));
  const register = useAction(async () => signIn(await api.auth.registerWithPin({ phone, name: name.trim(), pin, activationCode: code || undefined })));
  const reset = useAction(async () => signIn(await api.auth.resetPin({ phone, activationCode: code, pin })));

  const submitPhone = () => {
    if (!PHONE.test(phone)) {
      setPhoneError('Số điện thoại gồm 10 số, bắt đầu bằng 0');
      return;
    }
    setPhoneError(null);
    void status.run();
  };

  const pinMismatch = pinAgain.length === 6 && pin !== pinAgain ? 'Hai lần nhập PIN chưa khớp' : null;
  const newPinReady = SIX_DIGITS.test(pin) && pin === pinAgain;
  const canRegister = name.trim().length > 0 && newPinReady && (needsCode ? SIX_DIGITS.test(code) : code === '' || SIX_DIGITS.test(code));
  const canReset = SIX_DIGITS.test(code) && newPinReady;
  const heading = HEADINGS[step];

  const pinField = (label: string, value: string, onChange: (v: string) => void, onSubmit?: () => void, error?: string | null) => (
    <Field
      label={label}
      placeholder="••••••"
      keyboardType="number-pad"
      inputMode="numeric"
      secureTextEntry
      maxLength={6}
      value={value}
      onChangeText={(t) => onChange(digits(t))}
      onSubmitEditing={onSubmit}
      error={error}
      style={{ letterSpacing: 6, fontSize: 18, fontWeight: '700' }}
    />
  );

  const codeField = (label: string) => (
    <Field
      label={label}
      placeholder="6 số in trên hóa đơn"
      keyboardType="number-pad"
      inputMode="numeric"
      autoComplete="one-time-code"
      maxLength={6}
      value={code}
      onChangeText={(t) => setCode(digits(t))}
      style={{ letterSpacing: 4, fontWeight: '700' }}
    />
  );

  return (
    <Screen background={colors.surface} maxWidth={480}>
      <View style={{ backgroundColor: colors.sky, paddingTop: 16, paddingHorizontal: 20, paddingBottom: 40, gap: 18 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
          <LogoWordmark />
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Đóng"
            onPress={() => back('/')}
            style={{ width: 44, height: 44, borderRadius: 22, backgroundColor: 'rgba(255,255,255,.35)', alignItems: 'center', justifyContent: 'center' }}
          >
            <Icon name="close" size={22} color={colors.text} />
          </Pressable>
        </View>
        <View>
          <Text>{step === 'phone' ? heading.kicker : `${heading.kicker} · ${phone}`}</Text>
          <Text variant="display">{heading.title}</Text>
        </View>
      </View>

      <View style={{ marginTop: -18, backgroundColor: colors.surface, borderTopLeftRadius: 24, borderTopRightRadius: 24, padding: 20, gap: 16 }}>
        {step === 'phone' ? (
          <Stack gap={16}>
            <Text variant="caption">Tích điểm mỗi lần mua tại quán và đổi quà thành viên. Chỉ cần số điện thoại và mã PIN 6 số.</Text>
            <Field
              label="Số điện thoại"
              placeholder="0905 xxx xxx"
              keyboardType="phone-pad"
              autoComplete="tel"
              inputMode="tel"
              maxLength={10}
              value={phone}
              onChangeText={(t) => setPhone(t.replace(/\D/g, ''))}
              onSubmitEditing={submitPhone}
              error={phoneError}
            />
            <InlineError message={status.error} />
            <Button label="Tiếp tục" onPress={submitPhone} loading={status.pending} />
          </Stack>
        ) : null}

        {step === 'pin' ? (
          <Stack gap={16}>
            {pinField('Mã PIN', pin, setPin, () => SIX_DIGITS.test(pin) && void login.run())}
            <InlineError message={login.error} />
            <Button label="Đăng nhập" onPress={() => void login.run()} loading={login.pending} disabled={!SIX_DIGITS.test(pin)} />
            <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
              <Button label="Đổi số điện thoại" variant="ghost" size="md" onPress={() => goTo('phone')} />
              <Button label="Quên PIN?" variant="ghost" size="md" onPress={() => goTo('reset')} />
            </View>
          </Stack>
        ) : null}

        {step === 'register' ? (
          <Stack gap={16}>
            {needsCode ? (
              <Notice>Số này đã có tài khoản nhưng chưa đặt PIN. Nhập mã kích hoạt in trên hóa đơn, hoặc nhờ thu ngân cấp mã.</Notice>
            ) : (
              <Text variant="caption">Mua tại quán và đọc số điện thoại cho thu ngân: hóa đơn sẽ có mã kích hoạt để xác minh tài khoản và đổi quà.</Text>
            )}
            <Field label="Tên của bạn" placeholder="Để quán gọi tên" autoComplete="name" value={name} onChangeText={setName} maxLength={80} />
            {pinField('Mã PIN (6 số)', pin, setPin)}
            {pinField('Nhập lại mã PIN', pinAgain, setPinAgain, undefined, pinMismatch)}
            {codeField(needsCode ? 'Mã kích hoạt' : 'Mã kích hoạt (nếu có)')}
            <InlineError message={register.error} />
            <Button label={code ? 'Tạo tài khoản & xác minh' : 'Tạo tài khoản'} onPress={() => void register.run()} loading={register.pending} disabled={!canRegister} />
            <Button label="Đổi số điện thoại" variant="ghost" size="md" onPress={() => goTo('phone')} />
          </Stack>
        ) : null}

        {step === 'reset' ? (
          <Stack gap={16}>
            <Notice>Đến quầy và nhờ thu ngân cấp mã kích hoạt (hiệu lực 24 giờ), rồi nhập mã đó và PIN mới.</Notice>
            {codeField('Mã kích hoạt')}
            {pinField('Mã PIN mới', pin, setPin)}
            {pinField('Nhập lại mã PIN mới', pinAgain, setPinAgain, undefined, pinMismatch)}
            <InlineError message={reset.error} />
            <Button label="Đặt lại PIN & đăng nhập" onPress={() => void reset.run()} loading={reset.pending} disabled={!canReset} />
            <Button label="Quay lại" variant="ghost" size="md" onPress={() => goTo('pin')} />
          </Stack>
        ) : null}
      </View>
    </Screen>
  );
};

const Notice = ({ children }: { children: string }) => (
  <View style={{ backgroundColor: colors.goldSoft, borderRadius: 12, padding: 10 }}>
    <Text variant="caption" color={colors.goldText}>
      {children}
    </Text>
  </View>
);
